import { collection, getDocs, or, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { RULES_SAFE_IN_CHUNK, TWO_IN_CHUNK } from './firestoreLimits';

// Chunk sizes live in firestoreLimits.js — the rules document-access budget
// (8 for a lone `in`) and the 30-disjunction cap (7 when combined with the
// four quarter labels). Read the comment there before changing either.
const IN_CHUNK = RULES_SAFE_IN_CHUNK;
const IN_CHUNK_WITH_QUARTERS = TWO_IN_CHUNK;

/** Canonical quarter labels for a school year: 2026 → ["2026Q1", …, "2026Q4"]. */
export function schoolYearQuarterLabels(startYear) {
  return ['Q1', 'Q2', 'Q3', 'Q4'].map((q) => `${startYear}${q}`);
}

/**
 * Role-aware "my classes" query.
 *
 * Teachers (including combined teacher+EA claims) keep the or() shape. An
 * EA-only token gets ONLY the assistantEmails clause: Firestore list rules
 * must prove every or() disjunct, and the `teacherId == uid` disjunct has no
 * allowing branch without the teacher claim — the whole or() query is denied
 * for pure EAs (2026-07 audit, finding 3b#3).
 */
export function buildMyClassesQuery(user) {
  const email = user?.email ? user.email.toLowerCase() : 'nobody@nothing.com';
  const assistantClause = where('assistantEmails', 'array-contains', email);
  const role = user?.role;
  if (role?.ea && !role?.teacher && !role?.admin) {
    return query(collection(db, 'class'), assistantClause);
  }
  return query(
    collection(db, 'class'),
    or(where('teacherId', '==', user.uid), assistantClause)
  );
}

/**
 * Chunked `classId in …` fetch with all chunks issued in parallel.
 *
 * Pass `quarterLabels` (schoolYearQuarterLabels) to scope assessment reads to
 * one school year SERVER-SIDE. The quarter field is the canonical
 * `{startYear}Q{1-4}` string, so this is exactly equivalent to the old
 * client-side isQuarterInSchoolYear filter — but a class's prior-year history
 * no longer gets downloaded just to be thrown away (2026-07 audit, step 1b).
 *
 * Returns raw query doc snapshots (callers map/filter as before).
 */
export async function getDocsByClassIds(collectionName, classIds, { quarterLabels = null } = {}) {
  if (!classIds || classIds.length === 0) return [];
  const size = quarterLabels ? IN_CHUNK_WITH_QUARTERS : IN_CHUNK;
  const chunks = [];
  for (let i = 0; i < classIds.length; i += size) {
    chunks.push(classIds.slice(i, i + size));
  }
  const snapshots = await Promise.all(
    chunks.map((chunk) => {
      const clauses = [where('classId', 'in', chunk)];
      if (quarterLabels) clauses.push(where('quarter', 'in', quarterLabels));
      return getDocs(query(collection(db, collectionName), ...clauses));
    })
  );
  return snapshots.flatMap((snap) => snap.docs);
}
